/**
 * Getting back to a file without re-picking it.
 *
 * Where the browser supports the File System Access API (Chrome, Edge), the
 * handle from the picker can be stored in IndexedDB and asked for the file
 * again next visit — that is what makes "recently opened" clickable. Elsewhere
 * (Firefox, Safari) there is no such handle, so recents are just names and the
 * user re-picks the file; everything else works identically.
 *
 * Handles, like everything here, live in this browser only.
 */

const DB_NAME = 'csv-explorer-handles'
const STORE = 'handles'
const RECENT_KEY = 'csv-explorer.recent'
const MAX_RECENT = 8

export const canPickFiles = typeof window !== 'undefined' && 'showOpenFilePicker' in window

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function withStore(mode, fn) {
  const db = await openDb()
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const request = fn(tx.objectStore(STORE))
      // Read the request's result only once the transaction is complete; a get
      // that found nothing resolves to undefined, never to the request itself.
      tx.oncomplete = () => resolve(request ? request.result : undefined)
      tx.onerror = () => reject(tx.error)
    })
  } finally {
    db.close()
  }
}

export async function rememberHandle(name, handle) {
  if (!handle) return
  try {
    await withStore('readwrite', (store) => store.put(handle, name))
  } catch {
    // Storage trouble only costs the shortcut, not the feature.
  }
}

export async function getHandle(name) {
  try {
    return (await withStore('readonly', (store) => store.get(name))) ?? null
  } catch {
    return null
  }
}

export async function forgetHandles() {
  try {
    await withStore('readwrite', (store) => store.clear())
  } catch {
    /* nothing to clear */
  }
}

/**
 * Ask a stored handle for its file again. Returns null when the user declines
 * permission or the file is gone — the caller falls back to the picker.
 */
export async function fileFromHandle(handle) {
  try {
    let state = await handle.queryPermission({ mode: 'read' })
    if (state === 'prompt') state = await handle.requestPermission({ mode: 'read' })
    if (state !== 'granted') return null
    return await handle.getFile()
  } catch {
    return null
  }
}

/* -------------------------------------------------------------------- recents */

export function loadRecent() {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** @param entry {name, size, hasHandle} */
export function pushRecent(entry) {
  const next = [entry, ...loadRecent().filter((x) => x.name !== entry.name)].slice(0, MAX_RECENT)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  return next
}

export function clearRecent() {
  localStorage.removeItem(RECENT_KEY)
  forgetHandles()
}
